/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import { useEffect, useState } from 'react';
import DropdownComponent from '../DropdownComponent';
import { useDispatch } from 'react-redux';
import { actions } from '../../store/Products';
import * as storageUtils from '../../utils/storageUtils';
import { useTranslations } from '../../hooks/useTranslations';
import TaskTitle from '@/components/TaskTitle/TaskTitle';
import { useAppSelector } from '@/hooks';
import { Text } from '@ui5/webcomponents-react';
import TaskIcon from '@/components/TaskIcon';

interface SelectedState {
    [key: string]: string;
    size: string;
    industry: string;
    region: string;
}

const PCK_COMPANY_INFORMATION = 'NULL_CompanyInformation';
const CompanyInformation: React.FC<any> = ({ content }) => {
    const dispatch = useDispatch();
    const { t } = useTranslations();
    const productList = useAppSelector(state => state.productList);

    const industry = [
        'AEROSPACE_AND_DEFENSE',
        'AGRIBUSINESS',
        'AUTOMOTIVE',
        'BANKING',
        'CHEMICALS',
        'COMMERCE',
        'CONSUMER_PRODUCTS',
        'CONSTRUCTION',
        'DEFENSE_AND_SECURITY',
        'ENGINEERING_CONSTRUCTION_AND_OPERATION',
        'FINANCE',
        'HEALTHCARE',
        'HIGH_TECH',
        'HIGHER_EDUCATION_AND_RESEARCH',
        'INDUSTRIAL_MANUFACTURING',
        'INSURANCE',
        'LIFE_SCIENCES',
        'MEDIA',
        'MILL_PRODUCTS_AND_MINING',
        'OIL_GAS_AND_ENERGY',
        'POSTAL_SERVICES',
        'PROFESSIONAL_SERVICES',
        'PUBLIC_SECTOR',
        'RETAIL',
        'SPORTS_AND_ENTERTAINMENT',
        'TELECOMMUNICATIONS',
        'TRAVEL_AND_TRANSPORTATIONS',
        'TECH',
        'UTILITIES',
        'WHOLESALE_DISTRIBUTION',
        'OTHERS',
    ];

    const size = ['0-200', '200-1,500', '1,500+'];
    const region = ['REGION_GLOBAL', 'REGION_EMEA', 'REGION_APJ', 'REGION_NA', 'REGION_LATAM'];

    const description = [t('COMPANY_INFORMATION_NUMBER_OF_EMPLOYEES'), t('COMPANY_INFORMATION_INDUSTRY'), t('COMPANY_INFORMATION_REGION')];

    let personalizationValue = content.personalizationConfiguration?.configurationValue;
    if (!personalizationValue) {
        personalizationValue = { size: '', industry: '', region: '' };
    } else {
        personalizationValue = JSON.parse(personalizationValue);
    }

    const [selected, setSelected] = useState<SelectedState>({
        size: personalizationValue.size,
        industry: personalizationValue.industry,
        region: personalizationValue.region,
    });

    const handleChange = (event: any, dropdown: string) => {
        setSelected(prevState => ({
            ...prevState,
            [dropdown]: event.detail.selectedOption.dataset.value,
        }));
    };

    useEffect(() => {
        updateStateVariable();
    }, [selected]);

    function updateStateVariable() {
        const USER_ID = storageUtils.getItem('user').user_uuid;
        dispatch(
            actions.setCurrentProductConfigurationValue({
                productRunId: productList.currentRunId,
                taskId: content.id,
                configurationKey: PCK_COMPANY_INFORMATION,
                configurationValue: JSON.stringify(selected),
                createdBy: USER_ID,
                updatedBy: USER_ID,
                personalizationConfiguration: content.personalizationConfiguration,
            })
        );
        dispatch(
            actions.setCurrentProductConfigKeys({
                key: PCK_COMPANY_INFORMATION,
                value: JSON.stringify(selected),
            })
        );
    }

    return (
        <div className="content__datacontroller" data-testid="company-information">
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    flexDirection: 'column',
                    width: '100%',
                }}
            >
                <TaskIcon icon="building" />
                <TaskTitle />
                <Text data-testid="company-information-question" style={{ marginBottom: '1rem' }}>
                    {t('COMPANY_INFORMATION_QUESTION_TEXT')}
                </Text>
                {Object.keys(selected).map((key, index) => {
                    let options;
                    if (key === 'size') {
                        options = size;
                    } else if (key === 'industry') {
                        options = industry;
                    } else {
                        options = region;
                    }

                    return (
                        <DropdownComponent
                            key={key}
                            label={description[index]}
                            dropdownKey={key}
                            selectedValue={selected[key]}
                            options={options}
                            handlechange={handleChange}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default CompanyInformation;
